import submit from "submit-form";
import moment from "moment";
import round from "lodash/round";
import i18n from "../i18n";

const nutrientRegex = /^[0-9]{1}\d*\.?(?:\d{1})?$/;

export const validateNutritionPlan = (
  plan,
  editPlan,
  copy,
  coachAvatar,
  firebaseProjectId,
  assert,
  flash,
  nutritionPlans
) => {
  try {
    const {
      calories,
      proteins,
      carbs,
      fats,
      fibers,
      startDate,
      endDate,
      startingVar,
      proteinsPercent,
      carbsPercent,
      fatsPercent,
      restDayActive,
      restDayProteins,
      restDayCarbs,
      restDayFats,
      restDayFibers,
      restDayCalories,
      restDayProteinsPercent,
      restDayCarbsPercent,
      restDayFatsPercent,
      notes,
    } = plan;
    const _id = editPlan && editPlan._id;

    assert(startDate, 400, i18n.t("common:validation.startDateRequired"));
    assert(endDate, 400, i18n.t("common:validation.endDateRequired"));
    assert(
      !moment(endDate).isSameOrBefore(moment(startDate)),
      400,
      i18n.t("common:validation.endDateAfterStartDate")
    );
    assert(
      !nutritionPlanOverlaps(plan, _id, nutritionPlans),
      400,
      i18n.t("common:validation.planOverlapsDateRange")
    );

    assert(proteins, 400, i18n.t("common:validation.proteinRequired"));
    assert(
      nutrientRegex.test(proteins),
      400,
      i18n.t("common:validation.proteinMustBePositive")
    );
    assert(carbs, 400, i18n.t("common:validation.carbsRequired"));
    assert(
      nutrientRegex.test(carbs),
      400,
      i18n.t("common:validation.carbsMustBePositive")
    );
    assert(fats, 400, i18n.t("common:validation.fatRequired"));
    assert(
      nutrientRegex.test(fats),
      400,
      i18n.t("common:validation.fatMustBePositive")
    );
    assert(calories, 400, i18n.t("common:validation.caloriesRequired"));
    assert(
      nutrientRegex.test(calories),
      400,
      i18n.t("common:validation.caloriesMustBePositive")
    );

    if (fibers) {
      assert(
        nutrientRegex.test(fibers),
        400,
        i18n.t("common:validation.fiberMustBePositive")
      );
    }

    if (startingVar === "kcal") {
      assert(
        proteinsPercent,
        400,
        i18n.t("common:validation.proteinPercentRequired")
      );
      assert(
        carbsPercent,
        400,
        i18n.t("common:validation.carbsPercentRequired")
      );
      assert(fatsPercent, 400, i18n.t("common:validation.fatPercentRequired"));

      const pfcPercent = round(
        Number(proteinsPercent) + Number(carbsPercent) + Number(fatsPercent),
        1
      );
      const pfcDelta = round(pfcPercent - 100, 1);

      assert(
        !(pfcDelta > 0),
        400,
        i18n.t("common:validation.pfcExceedsTarget", { delta: pfcDelta })
      );
      assert(
        !(pfcDelta < 0),
        400,
        i18n.t("common:validation.pfcBelowTarget", { delta: pfcDelta })
      );
    }

    if (restDayActive) {
      assert(
        restDayProteins,
        400,
        i18n.t("common:validation.restDayProteinRequired")
      );
      assert(
        nutrientRegex.test(restDayProteins),
        400,
        i18n.t("common:validation.restDayProteinMustBePositive")
      );
      assert(
        restDayCarbs,
        400,
        i18n.t("common:validation.restDayCarbsRequired")
      );
      assert(
        nutrientRegex.test(restDayCarbs),
        400,
        i18n.t("common:validation.restDayCarbsMustBePositive")
      );
      assert(restDayFats, 400, i18n.t("common:validation.restDayFatRequired"));
      assert(
        nutrientRegex.test(restDayFats),
        400,
        i18n.t("common:validation.restDayFatMustBePositive")
      );
      assert(
        restDayCalories,
        400,
        i18n.t("common:validation.restDayCaloriesRequired")
      );
      assert(
        nutrientRegex.test(restDayCalories),
        400,
        i18n.t("common:validation.restDayCaloriesMustBePositive")
      );

      if (restDayFibers) {
        assert(
          nutrientRegex.test(restDayFibers),
          400,
          i18n.t("common:validation.restDayFiberMustBePositive")
        );
      }

      if (startingVar === "kcal") {
        assert(
          restDayProteinsPercent,
          400,
          i18n.t("common:validation.restDayProteinPercentRequired")
        );
        assert(
          restDayCarbsPercent,
          400,
          i18n.t("common:validation.restDayCarbsPercentRequired")
        );
        assert(
          restDayFatsPercent,
          400,
          i18n.t("common:validation.restDayFatPercentRequired")
        );

        const pfcPercent = round(
          Number(restDayProteinsPercent) +
            Number(restDayCarbsPercent) +
            Number(restDayFatsPercent),
          1
        );
        const pfcDelta = round(pfcPercent - 100, 1);

        assert(
          !(pfcDelta > 0),
          400,
          i18n.t("common:validation.pfcExceedsTargetRestDay", {
            delta: pfcDelta,
          })
        );
        assert(
          !(pfcDelta < 0),
          400,
          i18n.t("common:validation.pfcBelowTargetRestDay", {
            delta: pfcDelta,
          })
        );
      }
    }

    const body = {
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
      calories: Number(calories),
      proteins: Number(proteins),
      carbs: Number(carbs),
      fats: Number(fats),
      avatar: coachAvatar,
      firebaseProjectId: firebaseProjectId,
    };

    if (fibers) {
      body.fibers = Number(fibers);
    }

    if (restDayActive) {
      body.restDay = {
        proteins: restDayProteins,
        carbs: restDayCarbs,
        fats: restDayFats,
        calories: restDayCalories,
      };

      if (restDayFibers) {
        body.restDay.fibers = restDayFibers;
      }
    } else {
      body.restDay = null;
    }

    if (notes) {
      body.notes = notes;
    }

    // When duplicating (copy), send the source plan's meal plan id only if user chose to duplicate meal plan (GET returns mealPlan populated, not mealPlanId)
    if (editPlan && copy && plan.duplicateMealPlan) {
      const mealPlanId = editPlan.mealPlanId ?? editPlan.mealPlan?._id;
      if (mealPlanId) body.mealPlanId = mealPlanId;
    }

    return body;
  } catch (err) {
    flash("error", err.message);
    submit("/actions/refresh");
    return false;
  }
};

const nutritionPlanOverlaps = (plan, id = null, list) => {
  // filter out the plan being edited
  const filtered = id ? list.filter(({ _id }) => _id !== id) : list;

  const start = Number(moment(plan.startDate).format("YYYYMMDD"));
  const end = Number(moment(plan.endDate).format("YYYYMMDD"));

  const overlapPlan = filtered.find((item) => {
    if (start >= item.utcStartDate && start <= item.utcEndDate) {
      // submitted plan start overlaps
      return true;
    } else if (end >= item.utcStartDate && end <= item.utcEndDate) {
      // submitted plan end overlaps
      return true;
    } else if (start <= item.utcStartDate && end >= item.utcEndDate) {
      // a plan exists between the submitted plan's date range
      return true;
    }
    return false;
  });

  return !!overlapPlan;
};
